import {makeAutoObservable} from 'mobx';
import request from 'superagent';
import QueryStringSerializer from './QueryStringSerializer';
import Row, {ROW_TYPE_ALL, ROW_TYPE_SIMPLE} from './models/Row';

/*
 * the grid is an array of row objects.
 *
 * row objects can be of two basic types, simple and subrows
 * ROW_TYPE_SIMPLE – representing individual products, or
 * ROW_TYPE_SUBROW – representing one of various downloads for the parent row
 * subrows are those which are shown when someone expands a simple row
 *
 * row objects can have the following properties:
 *
 * row.label | string | label
 * row.type | int | ROW_TYPE_SIMPLE|ROW_TYPE_SUBROW|ROW_TYPE_ALL – type of row
 * row.idx | int | index ascending from zero for all rows (except subrows)
 * row.group | string | an optional “group” label e.g. for a family of products
 * row.areas | string | used to filter rows by area (e.g. “interior”/“exterior”)
 * row.filtertags | array[string…] | optional filters which apply to this row
 * row.uri | string | related kirby uri (e.g. “kju/kju-circle”) used internally to build download URIs
 * row.isExpandedAt | int | idx of slot which is “open” (expanded) i.e. the download type – subrows only
 * row.slots | array[{}…] | array of slot data objects – one for each download type – see below
 *
 * a slot – a single cell in a simple row
 * each simple row has an array of slots with properties
 *
 * slot.downloads | array[{}…] | 0…n download file objects see below
 * slot.isExpanded | bool | true if this slot is expanded to show expanded download rows
 * slot.selected | bool | true if the slot is selected for download
 * slot.all | bool | indicates a slot in the “all” column
 *
 * each slot has an array of 0…n individual downloads (files)
 * for slots of type “multi”, expanded rows can be displayed for each variant download using the download.label field
 * download objects have the following with properties
 *
 * download.label | label | the label for the individual download
 * download.uri | string | filename of the individual download
 *
 */

class DownloadStore {
	isDownloading = false;

	grid;
	filters;
	config;
	downloadTypes;
	onlyFamily = false;

	constructor(data) {
		this.downloadTypes = data.downloadTypes;
		this.filters = data.filters;
		this.config = data.config;
		this.filterGroups = data.filterGroups;
		this.onlyFamily = data.onlyFamily;
		this.grid = [
			new Row(ROW_TYPE_ALL, 0, {
				areas: [],
				group: data.translations['downloads.all'],
				slots: Array.from(Array(this.downloadTypes.length)).map(() => ({all: true})),
			}, this.downloadTypes),
		];
		data.grid.forEach((rowData, y) => {
			this.grid.push(new Row(ROW_TYPE_SIMPLE, y + 1, rowData, this.downloadTypes));
		});
		makeAutoObservable(this);
		this.resetFilters(false, false);
		if (this.onlyFamily) {
			this.setSelectedArea(this.getAreaForFamilyKey(this.onlyFamily), false);
			this.toggleFilter(this.onlyFamily, false);
		} else {
			QueryStringSerializer.unserialize(this);
		}
	}

	get selectedArea() {
		const selected = this.filters.find((a) => a.active);
		return typeof selected === 'undefined' ? false : selected;
	}

	get availableFilters() {
		return Object.values(
			this.selectedArea.filters.reduce((acc, filter) => {
				if (typeof acc[filter.group] === 'undefined') {
					acc[filter.group] = {key: filter.group, filters: []};
				}
				acc[filter.group].filters.push(filter);
				return acc;
			}, {})
		).sort((a, b) => this.filterGroups.indexOf(a.key) - this.filterGroups.indexOf(b.key));
	}

	get selectedFilters() {
		return this.selectedArea.filters.reduce((acc, f) => {
			if (f.active) {
				acc.push(f);
			}
			return acc;
		}, []);
	}

	get prettySelectedFilters() {
		const prettySelected = [];
		for (const filter of this.selectedArea.filters) {
			for (const selected of this.selectedFilters) {
				if (filter.key === selected.key) {
					prettySelected.push(selected);
				}
			}
		}
		if (prettySelected.length === 0) return false;
		return prettySelected;
	}

	getAreaForFamilyKey(familyKey) {
		let areaKey = '';
		for (const area of this.filters) {
			for (const filter of area.filters) {
				if (filter.key === familyKey) {
					areaKey = area.key;
					break;
				}
			}
			if (areaKey.length > 0) break;
		}
		return areaKey;
	}

	setSelectedArea(key, resetFilters = true) {
		if (this.selectedArea !== false && this.selectedArea.key === key) return;
		let activated = false;
		for (const area of this.filters) {
			area.active = false;
			if (area.key === key) {
				area.active = true;
				activated = true;
			}
		}
		if (!activated) {
			this.filters[0].active = true;
		}
		if (resetFilters) {
			this.resetFilters();
		}
	}

	resetFilters(onlyGroup = false, serialize = true) {
		for (const area of this.filters) {
			for (const f of area.filters) {
				if (onlyGroup === false || f.group === onlyGroup) {
					f.active = false;
				}
			}
		}
		this.updateDisabledFilters();
		if (serialize) {
			QueryStringSerializer.serialize(this); // hmmm
		}
	}

	toggleFilter(filterKey, serializeToQuery = true) {
		for (const filter of this.selectedArea.filters) {
			if (filter.key === filterKey) {
				filter.active = !filter.active;
				break;
			}
		}
		this.updateDisabledFilters();
		if (serializeToQuery) {
			QueryStringSerializer.serialize(this);
		}
	}

	/**
	 * disable any filters which would match no rows if combined with the currently selected filters
	 */
	updateDisabledFilters() {
		if (!this.selectedArea) return;
		const disabled = [];
		for (const f of this.selectedArea.filters) {
			// loop over all of the area’s filters
			if (f.active) continue; // active filters will never be disabled
			let matches = false;
			for (const row of this.rowsInSelectedArea) {
				// go over all the rows
				const filterGroups = JSON.parse(JSON.stringify(this.selectedFiltersGrouped)); // deep clone
				if (!filterGroups[f.group]) filterGroups[f.group] = []; // guarantee group keys
				filterGroups[f.group].push(f);
				if (this.testRowWithFilters(row, filterGroups)) {
					matches = true;
					break;
				}
			}
			if (!matches) {
				disabled.push(f.key);
			}
		}
		for (const f of this.selectedArea.filters) {
			f.disabled = disabled.indexOf(f.key) > -1;
		}
	}

	/**
	 * Get the filtered data grid – used for display in Components, includes “subrows”
	 */
	get filteredGrid() {
		return this.getExpandedRows(this.filteredRows);
	}

	/**
	 * Get the filtered data grid, with ALL expandable rows expanded
	 */
	get filteredGridAllExpanded() {
		return this.getExpandedRows(this.filteredRows, true);
	}

	/**
	 * Get the filtered rows of the data grid
	 */
	get filteredRows() {
		return this.rowsInSelectedArea.filter((row, idx) => {
			if (idx === 0) return true; // always include the special “all“ row
			return this.testRowWithFilters(row, this.selectedFiltersGrouped);
		});
	}

	testRowWithFilters(row, groupedFilters) {
		const includeInGroups = [];
		for (const filterGroup of Object.values(groupedFilters)) {
			// does this row have AT LEAST ONE of the selected filter tags from the current group (logical OR)
			includeInGroups.push(filterGroup.filter((filter) => row.filtertags.indexOf(filter.key) !== -1).length > 0);
		}
		// does this row have a tag for ALL active groups (local AND)
		return includeInGroups.filter((g) => g).length === includeInGroups.length;
	}

	get rowsInSelectedArea() {
		return this.grid.filter((row, idx) => {
			if (idx === 0) return true; // always include the special “all“ row
			if (!row.filtertags) return false; // do not include rows without any filters
			return row.areas.indexOf(this.selectedArea.key) > -1; // only include rows in the current area
		});
	}

	get selectedFiltersGrouped() {
		return this.selectedFilters.reduce((acc, f) => {
			if (typeof acc[f.group] === 'undefined') {
				acc[f.group] = [];
			}
			acc[f.group].push(f);
			return acc;
		}, {});
	}

	getExpandedRows(rows, expandAll = false) {
		const resultRows = [...rows]; // shallow clone
		// synthesize extra “variant rows” for expanded rows
		const expandingRows = resultRows.filter((row) => (expandAll ? true : row.isExpanded));
		for (const expandedRow of expandingRows) {
			const expandedRowIdx = resultRows.findIndex((row) => row.idx === expandedRow.idx);
			const expandedAtSlotIdx = expandedRow.slots.findIndex((slot) => slot.isExpanded);
			if (expandedAtSlotIdx !== -1) {
				const expandedSlot = expandedRow.slots[expandedAtSlotIdx];
				const expandedRows = expandedSlot.downloads.map((download) => expandedRow.createSubrow(download, expandedAtSlotIdx, this.downloadTypes));
				resultRows.splice(...[expandedRowIdx + 1, 0].concat(expandedRows));
			}
		}
		return resultRows;
	}

	toggleSlotSelection(row, slot, multiViewSpecsheetToggleClick = false) {
		const slotIdx = row.slots.indexOf(slot);
		if (row.isAll) {
			// de/select entire column
			const unselected = this.filteredGrid.filter((r) => r.slots[slotIdx].isUnselected);
			const select = unselected.length > 0;
			for (const r of this.filteredGrid) {
				if (r.isAll) continue;
				const targetSlot = r.slots[slotIdx];
				if (targetSlot) {
					targetSlot.toggleSelection(select);
				}
			}
		} else if (slot.all) {
			// de/select entire row
			const select = !row.allSelected;
			for (const siblingSlot of row.slots) {
				siblingSlot.toggleSelection(select);
			}
		} else if (slot.isMulti) {
			// close all other slots
			for (const siblingSlot of row.slots) {
				if (siblingSlot.isMulti && siblingSlot !== slot) {
					siblingSlot.isExpanded = false;
				}
			}
			let expand = false;
			if (multiViewSpecsheetToggleClick) {
				row.viewSpecsheetExpanded = !row.viewSpecsheetExpanded;
				expand = row.viewSpecsheetExpanded;
			} else {
				if (slot.isExpanded) {
					if (row.viewSpecsheetExpanded) {
						expand = slotIdx === 0;
					}
				} else {
					expand = true;
				}
				row.viewSpecsheetExpanded = false;
			}
			slot.isExpanded = expand;
		} else if (slot.isSingle) {
			// toggle an actual download selection
			slot.toggleSelection();
		}
	}

	get selectedForDownload() {
		return this.filteredGridAllExpanded.reduce((allSelections, row) => allSelections.concat(row.selectedForDownload), []);
	}

	get numSelectedDownloads() {
		return this.selectedForDownload.length;
	}

	showPDF(url) {
		window.open(url, '_blank');
	}

	download() {
		const toDownload = this.selectedForDownload;
		if (toDownload.length > 0) {
			const uri = document.body.dataset.slxDownloadUrl;
			this.isDownloading = true;
			request
				.post(uri)
				.send({downloads: toDownload.map(d => d.uri)})
				.set('Accept', 'application/zip')
				.responseType('blob')
				.end((error, response) => {
					this.isDownloading = false;
					if (error) {
						return;
					}
					if (response) {
						let filename = 'selux-download.zip';
						const contentDisposition = response.headers['content-disposition'];
						if (contentDisposition) {
							filename = contentDisposition.substr(contentDisposition.indexOf('=') + 1);
						}
						const el = document.createElement('a');
						el.setAttribute('download', filename);
						el.setAttribute('href', window.URL.createObjectURL(response.xhr.response));
						document.body.appendChild(el);
						el.click();
						document.body.removeChild(el);
					}
				});
			// eslint-disable-next-line piggyback/no-restricted-global-extend
			const {dataLayer, gtag} = window;
			if (dataLayer) {
				const dispatchEvent = (type, products, files) => {
					gtag('event', 'download', {
						event_category: 'Downloads',
						event_label: 'File Downloads',
						// custom GA dimensions
						downloaded_type: type,
						downloaded_products: products,
						downloaded_files: files
					});
				};
				if (toDownload.length > 20) {
					dispatchEvent(
						'mass file download',
						'multiple products',
						'multiple files'
					);
				} else {
					for (const download of toDownload) {
						dispatchEvent(
							download.type,
							download.subproduct,
							download.uri.split('/').pop()
						);
					}
				}
			}
		}
	}
}

export default DownloadStore;
